import { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';
import CryptoJS from 'crypto-js';

const SECRET_KEY = 'tabtrips_hotel_secret';
const TOKEN_COOKIE_KEY = 'token';

function getDecryptedToken() {
  const encrypted = Cookies.get(TOKEN_COOKIE_KEY);
  if (!encrypted) return null;
  try {
    const bytes = CryptoJS.AES.decrypt(encrypted, SECRET_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    return null;
  }
}

function removeToken() {
  Cookies.remove(TOKEN_COOKIE_KEY);
}

/**
 * Custom hook to handle token validation and API errors
 * @param {Function} apiCallback - The API call function to execute
 * @param {Array} dependencies - Dependencies array for useEffect
 */
const useTokenValidation = (apiCallback, dependencies = []) => {
  const navigate = useNavigate();
  const { handleInvalidToken } = useContext(AuthContext);

  // Function to handle API errors
  const handleApiError = (error) => {
    console.error('API Error:', error);
    
    // Check for authorization errors
    if (error.response) {
      if (error.response.status === 401 || error.response.status === 403) {
        handleInvalidToken();
        return;
      }
      
      // Handle other status codes
      if (error.response.data && error.response.data.message) {
        // toast.error(error.response.data.message); // Disabled
        return;
      }
    }
    
    // Generic error
    // toast.error('An error occurred. Please try again.'); // Disabled
  };

  // Function to handle API response
  const handleApiResponse = (response) => {
    if (!response.status) {
      // Check for token-related errors in the message
      const message = response.message || '';
      if (
        message.toLowerCase().includes('token') || 
        message.toLowerCase().includes('unauthorized') ||
        message.toLowerCase().includes('auth') ||
        message.toLowerCase().includes('login')
      ) {
        handleInvalidToken();
        return false;
      }
      
      // Other API error
      // toast.error(message || 'Operation failed'); // Disabled
      return false;
    }
    
    return true;
  };

  useEffect(() => {
    const token = getDecryptedToken();
    if (!token) {
      removeToken();
      handleInvalidToken();
      return;
    }

    const executeApi = async () => {
      try {
        const response = await apiCallback();
        handleApiResponse(response);
      } catch (error) {
        handleApiError(error);
      }
    };

    executeApi();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies, navigate, handleInvalidToken]);

  return { handleApiError, handleApiResponse };
};

export default useTokenValidation; 